import { cn } from "@/lib/utils";
import { DECISION_STYLE, LIFECYCLE_STYLE, SEVERITY_STYLE } from "@/lib/format";
import { BrandSpinner } from "@/components/common/BrandSpinner";

export const Panel = ({ title, kicker, actions, children, className, testId }) => (
  <section className={cn("panel relative overflow-hidden", className)} data-testid={testId}>
    {(title || actions) && (
      <header className="panel-head">
        <div>
          {kicker && <div className="kicker">{kicker}</div>}
          {title && <h3 className="text-base font-semibold text-foreground">{title}</h3>}
        </div>
        {actions}
      </header>
    )}
    {children}
  </section>
);

const ACCENT = {
  sky: "bg-primary/10 text-primary",
  primary: "bg-primary/10 text-primary",
  emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  teal: "bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))]",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  red: "bg-red-500/10 text-red-600 dark:text-red-400",
  violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  slate: "bg-muted text-muted-foreground",
};

export const Kpi = ({ label, value, sub, accent = "primary", testId, icon: Icon }) => (
  <div className="panel group relative overflow-hidden p-4 transition-transform duration-200 hover:-translate-y-0.5" data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2">
        {Icon && (
          <span className={cn("grid h-8 w-8 place-items-center rounded-lg", ACCENT[accent] || ACCENT.primary)}>
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div className="text-[12px] font-medium text-muted-foreground">{label}</div>
      </div>
    </div>
    <div className="mt-3 font-display text-2xl font-bold tracking-tight tabular-nums text-foreground">
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const Chip = ({ children, className, testId, ...rest }) => (
  <span
    data-testid={testId}
    {...rest}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
      className,
    )}
  >
    {children}
  </span>
);

export const LifecycleChip = ({ state, testId }) => (
  <Chip testId={testId} className={LIFECYCLE_STYLE[state] || LIFECYCLE_STYLE.DRAFT}>
    {String(state || "").replace(/_/g, " ")}
  </Chip>
);

export const DecisionChip = ({ decision, testId }) => (
  <Chip testId={testId} className={DECISION_STYLE[decision] || DECISION_STYLE.PASS}>
    {String(decision || "").replace(/_/g, " ")}
  </Chip>
);

export const SeverityChip = ({ severity, testId }) => (
  <Chip testId={testId} className={SEVERITY_STYLE[severity] || SEVERITY_STYLE.inform}>
    {String(severity || "").replace(/_/g, " ")}
  </Chip>
);

export const PageHeader = ({ eyebrow, title, description, actions, className }) => (
  <div className={cn("flex flex-col gap-4 md:flex-row md:items-end md:justify-between", className)}>
    <div className="max-w-3xl">
      {eyebrow && <div className="kicker mb-2">{eyebrow}</div>}
      {title && <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>}
      {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Th = ({ children, className }) => (
  <th className={cn("whitespace-nowrap px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground", className)}>
    {children}
  </th>
);

export const Td = ({ children, className, ...rest }) => (
  <td className={cn("px-3 py-2.5 align-middle text-xs text-foreground/80 md:text-sm", className)} {...rest}>
    {children}
  </td>
);

export const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse" data-testid={testId}>
      <thead className="sticky top-0 bg-muted/60 backdrop-blur">
        <tr className="border-b border-border">{head}</tr>
      </thead>
      <tbody className="divide-y divide-border">{children}</tbody>
    </table>
  </div>
);

export const Empty = ({ label = "No records", colSpan = 6 }) => (
  <tr>
    <td colSpan={colSpan} className="px-3 py-10 text-center text-xs text-muted-foreground">
      {label}
    </td>
  </tr>
);

export const Loading = ({ label = "Loading" }) => (
  <div className="flex min-h-[52vh] w-full flex-col items-center justify-center gap-5 px-4 py-16 text-center text-[12.5px] text-muted-foreground"
       data-testid="loading-state">
    <BrandSpinner size={132} />
    <span className="animate-pulse">{label}…</span>
  </div>
);

export const PrimaryButtonClass =
  "rounded-full bg-primary text-primary-foreground shadow-sm shadow-primary/25 transition-transform duration-150 hover:bg-primary/90 active:scale-[0.98]";
